import React, { useState, useEffect, useMemo } from "react";
import "./TjedniOtpis.css";
import { ReportAPI } from "../../Api/ReportAPI";

function getWeekDuration(week, year) {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = new Date(simple);

  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }

  const ISOweekEnd = new Date(ISOweekStart);
  ISOweekEnd.setDate(ISOweekStart.getDate() + 6);

  return {
    start: ISOweekStart,
    end: ISOweekEnd,
  };
}

function getWeekDurationString(week, year) {
  const { start, end } = getWeekDuration(week, year);
  return `Tjedan od ${start.toLocaleDateString()} do ${end.toLocaleDateString()}`;
}

const WeeklyReport = () => {
  const [weeklyReports, setWeeklyReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );
  const [selectedWeekNumber, setSelectedWeekNumber] = useState(null);

  const [pdfReportUrl, setPdfReportUrl] = useState(null);
  const [xlsxReportUrl, setXlsxReportUrl] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchWeeklyReports = async () => {
      try {
        const response = await ReportAPI.getWeeklyReports();
        const reports = response.data || [];

        setWeeklyReports(reports);

        const years = [...new Set(reports.map((r) => r.year))].sort(
          (a, b) => b - a
        );

        const currentYear = new Date().getFullYear().toString();
        const newSelectedYear = years.includes(currentYear)
          ? currentYear
          : years[0] || currentYear;

        setSelectedYear(newSelectedYear);
      } catch (error) {
        console.error(error);
        setError("Došlo je do pogreške prilikom dohvaćanja izvještaja");
      } finally {
        setLoading(false);
      }
    };

    fetchWeeklyReports();
  }, []);

  const filteredReports = useMemo(() => {
    return weeklyReports
      .filter((r) => r.year === selectedYear)
      .sort((a, b) => parseInt(b.week, 10) - parseInt(a.week, 10));
  }, [weeklyReports, selectedYear]);

  const weeksArray = [
    ...new Set(filteredReports.map((r) => parseInt(r.week, 10).toString())),
  ];

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
    setSelectedWeekNumber(null);
    setPdfReportUrl(null);
    setXlsxReportUrl(null);
    setError(null);
  };

  const handleWeekClick = (weekNumber) => {
    setSelectedWeekNumber(weekNumber);
    setPdfReportUrl(null);
    setXlsxReportUrl(null);
    setError(null);
  };

  const selectedReports = useMemo(() => {
    if (!selectedWeekNumber) return [];
    return filteredReports.filter((r) => r.week === selectedWeekNumber);
  }, [filteredReports, selectedWeekNumber]);

  const selectedPdf = selectedReports.find((r) => r.type === "pdf");
  const selectedXlsx = selectedReports.find((r) => r.type === "xlsx");

  useEffect(() => {
    let cancelled = false;

    if (!selectedPdf) {
      setPdfReportUrl(null);
      return;
    }

    const fetchPdfPreviewUrl = async () => {
      try {
        setPdfPreviewLoading(true);
        const response = await ReportAPI.getPreviewFileUrl(selectedPdf.id);

        if (!cancelled) {
          setPdfReportUrl(response.data.url);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setError("Greška pri učitavanju PDF previewa");
          setPdfReportUrl(null);
        }
      } finally {
        if (!cancelled) {
          setPdfPreviewLoading(false);
        }
      }
    };

    fetchPdfPreviewUrl();

    return () => {
      cancelled = true;
    };
  }, [selectedPdf]);

  useEffect(() => {
    let blobUrl = null;

    if (!selectedXlsx) {
      setXlsxReportUrl(null);
      return;
    }

    const fetchXlsx = async () => {
      try {
        setXlsxLoading(true);
        const response = await ReportAPI.getReportFile(selectedXlsx.id, 2);
        const file = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        blobUrl = URL.createObjectURL(file);
        setXlsxReportUrl(blobUrl);
      } catch (error) {
        console.error(error);
        setError("Greška pri učitavanju XLSX datoteke");
      } finally {
        setXlsxLoading(false);
      }
    };

    fetchXlsx();

    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [selectedXlsx]);

  const handleDownloadPdf = async () => {
    if (!selectedPdf) return;

    try {
      setPdfLoading(true);
      const response = await ReportAPI.getReportFile(selectedPdf.id, 0);
      const file = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(file);

      const link = document.createElement("a");
      link.href = url;
      link.download = `otpis_${selectedPdf.year}-${selectedPdf.week}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      setError("Greška pri preuzimanju PDF izvještaja");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="weekly-report-container">
      <div className="header">
        <h2>Tjedni otpis svih Idenata na svim lokacijama</h2>
        <div className="year-selector">
          <label htmlFor="year">Odaberite godinu:</label>
          <select id="year" value={selectedYear} onChange={handleYearChange}>
            {[...new Set(weeklyReports.map((r) => r.year))]
              .sort((a, b) => b - a)
              .map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="spinner-container">
          <div className="spinner"></div>
          <p>Učitavanje izvještaja...</p>
        </div>
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
        </div>
      ) : (
        <>
          <div className="weekly-selector">
            {weeksArray.map((weekNumber) => (
              <div
                key={weekNumber}
                className={`week-circle available ${
                  selectedWeekNumber === weekNumber ? "selected" : ""
                }`}
                onClick={() => handleWeekClick(weekNumber)}
                data-tooltip={getWeekDurationString(
                  parseInt(weekNumber, 10),
                  selectedYear
                )}
              >
                {weekNumber}
              </div>
            ))}
          </div>

          {selectedWeekNumber && (
            <>
              <div className="report-options">
                {selectedPdf && (
                  <div className="pdf-report">
                    <h3>PDF izvještaj</h3>
                    {pdfLoading ? (
                      <div className="spinner-container">
                        <div className="spinner"></div>
                        <p>Preuzimanje PDF-a...</p>
                      </div>
                    ) : (
                      <div className="download-buttons-container">
                        <button
                          className="download-button"
                          onClick={handleDownloadPdf}
                          type="button"
                        >
                          Preuzmi PDF izvještaj
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {selectedXlsx && (
                  <div className="xlsx-report">
                    <h3>XLSX izvještaj</h3>
                    {xlsxLoading ? (
                      <div className="spinner-container">
                        <div className="spinner"></div>
                        <p>Učitavanje XLSX datoteke...</p>
                      </div>
                    ) : (
                      <div className="download-buttons-container">
                        <a
                          className="download-button"
                          href={xlsxReportUrl}
                          download={`otpis_${selectedXlsx.year}-${selectedXlsx.week}.xlsx`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Preuzmi XLSX izvještaj
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedPdf && (
                <div className="iframe-container" style={{ marginTop: "20px" }}>
                  {pdfPreviewLoading ? (
                    <div className="spinner-container">
                      <div className="spinner"></div>
                      <p>Učitavanje PDF previewa...</p>
                    </div>
                  ) : isMobile ? (
                    <div className="mobile-report">
                      <p>PDF pregled nije podržan na mobilnom uređaju.</p>
                      {pdfReportUrl ? (
                        <a
                          href={pdfReportUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Kliknite ovdje za otvaranje PDF izvještaja
                        </a>
                      ) : null}
                    </div>
                  ) : (
                    pdfReportUrl && (
                      <iframe
                        title={`Weekly report ${selectedPdf.year}-${selectedPdf.week}`}
                        src={pdfReportUrl}
                        width="100%"
                        height="1200px"
                        style={{ border: "none" }}
                      />
                    )
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default WeeklyReport;